// src/pages/Dashboard.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscribeToAuth } from '../auth';
import { API_URL } from '../config';
import { apiFetch, apiRequest } from '../api';
import { useDataChanged } from '../testMode/testMode';
import AppShell from '../components/AppShell';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import EditableName from '../components/EditableName';
import BackupsPanel from './BackupsPanel';
import NewTournamentDialog from '../components/NewTournamentDialog';
import { useCollaboration } from '../collab/useCollaboration';
import { formatDateRange, formatDay } from '../utils/time';
import { PlusIcon, TrashIcon, TrophyIcon, UsersIcon } from '../components/icons';

const jsonHeaders = { 'Content-Type': 'application/json' };

function Dashboard() {
    const [user, setUser] = useState(null);
    const [tournaments, setTournaments] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const navigate = useNavigate();
    const toast = useToast();
    const confirm = useConfirm();
    const collab = useCollaboration(user);

    useEffect(() => {
        const unsubscribe = subscribeToAuth((firebaseUser) => {
            if (!firebaseUser) {
                // If no user, redirect to login
                navigate('/');
            } else {
                setUser(firebaseUser);
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    // Fetch tournaments for the logged-in user
    const fetchTournaments = useCallback(async () => {
        try {
            const response = await apiFetch(`${API_URL}/getTournaments?userId=${user.uid}`);
            if (!response.ok) {
                throw new Error('Failed to fetch tournaments');
            }
            setTournaments(await response.json());
        } catch (error) {
            console.error('Error fetching tournaments:', error);
            toast.error('Could not load your tournaments.');
        } finally {
            setLoaded(true);
        }
    }, [user, toast]);

    // Called once user is set
    useEffect(() => {
        if (user) {
            fetchTournaments();
        }
    }, [user, fetchTournaments]);

    // The test panel changes tournaments behind our back.
    useDataChanged(() => {
        if (user) fetchTournaments();
    });

    const [settingUp, setSettingUp] = useState(false); // showing the new-tournament dialog

    // The dialog's choices, applied right after creating the bare tournament, before going to it: so the
    // choices that matter (the format especially) are made on purpose, not discovered later.
    const handleCreateTournament = async (choices) => {
        if (!user) return;
        try {
            const response = await apiFetch(`${API_URL}/createTournament`, {
                method: 'POST',
                headers: jsonHeaders,
                body: JSON.stringify({ userId: user.uid }),
            });
            if (!response.ok) {
                throw new Error('Failed to create tournament');
            }
            const newTournament = await response.json();
            const configured = await apiRequest('PATCH', `/tournaments/${newTournament.tournamentId}`, user.uid, choices);
            setTournaments((prev) => [...prev, configured]);
            setSettingUp(false);
            navigate(`/tournament/${newTournament.tournamentId}`);
        } catch (error) {
            console.error('Error creating tournament:', error);
            toast.error('Could not create the tournament.');
        }
    };

    // Resolves to true when the new name was saved (see EditableName).
    const handleRenameTournament = async (tournament, name) => {
        try {
            const updated = await apiRequest('PATCH', `/tournaments/${tournament.tournamentId}`, user.uid, { name });
            setTournaments((prev) =>
                prev.map((candidate) =>
                    candidate.tournamentId === tournament.tournamentId ? { ...candidate, ...updated } : candidate
                )
            );
            return true;
        } catch (error) {
            console.error('Error renaming the tournament:', error);
            toast.error(`Could not rename the tournament. ${error.message}`);
            return false;
        }
    };

    const handleDeleteTournament = async (tournament) => {
        const confirmed = await confirm({
            title: `Delete ${tournament.name}?`,
            message: 'Its players, rounds and results will be removed. This cannot be undone.',
            confirmLabel: 'Delete tournament',
        });
        if (!confirmed || !user) return;
        try {
            const response = await apiFetch(`${API_URL}/deleteTournament`, {
                method: 'DELETE',
                headers: jsonHeaders,
                body: JSON.stringify({ userId: user.uid, tournamentId: tournament.tournamentId }),
            });
            if (!response.ok) {
                throw new Error('Failed to delete tournament');
            }
            await fetchTournaments();
        } catch (error) {
            console.error('Error deleting tournament:', error);
            toast.error('Could not delete the tournament.');
        }
    };

    if (!user) {
        return null;
    }

    return (
        <AppShell user={user}>
            <div className="page-head">
                <div className="titles">
                    <h1>Your tournaments</h1>
                    <p className="muted">Create a tournament, add players, and play rounds.</p>
                </div>
                <div className="page-actions">
                    <button className="btn" onClick={() => navigate('/players')}>
                        Player statistics
                    </button>
                    <button className="btn btn-primary" onClick={() => setSettingUp(true)}>
                        <PlusIcon /> New tournament
                    </button>
                </div>
            </div>

            {settingUp && (
                <NewTournamentDialog userId={user.uid} onCreate={handleCreateTournament} onClose={() => setSettingUp(false)} />
            )}

            {!loaded ? (
                <div className="tournament-grid" aria-busy="true">
                    <div className="skeleton" />
                    <div className="skeleton" />
                    <div className="skeleton" />
                </div>
            ) : tournaments.length === 0 ? (
                <div className="empty">
                    <TrophyIcon size={36} />
                    <strong>No tournaments yet</strong>
                    <p>Create your first tournament to get started. Add players, plan rounds and enter scores; the app does the pairings and the ranking.</p>
                    <button className="btn btn-primary" onClick={() => setSettingUp(true)}>
                        <PlusIcon /> New tournament
                    </button>
                </div>
            ) : (
                <div className="tournament-grid">
                    {tournaments.map((tournament) => (
                        <article key={tournament.tournamentId} className="card tournament-card">
                            <div>
                                <EditableName
                                    as="h3"
                                    name={tournament.name}
                                    inputLabel="Tournament name"
                                    onSave={(name) => handleRenameTournament(tournament, name)}
                                />
                                {/* only when somebody is actually involved: a link that nobody has used yet says nothing */}
                                {collab(tournament).shared && (collab(tournament).role !== 'owner' || collab(tournament).coOrganizerCount > 0) && (
                                    <p className="muted card-date">
                                        <UsersIcon size={14} />{' '}
                                        {collab(tournament).role === 'owner'
                                            ? `Shared with ${collab(tournament).coOrganizerCount} co-organizer${collab(tournament).coOrganizerCount === 1 ? '' : 's'}`
                                            : 'You are a co-organizer'}
                                    </p>
                                )}
                                {(formatDateRange(tournament.startDate, tournament.endDate) || tournament.createdAt) && (
                                    <p className="muted card-date">
                                        {formatDateRange(tournament.startDate, tournament.endDate) ||
                                            `Created ${formatDay(tournament.createdAt)}`}
                                    </p>
                                )}
                            </div>
                            <div className="actions">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => navigate(`/tournament/${tournament.tournamentId}`)}
                                >
                                    Open
                                </button>
                                {collab(tournament).role === 'owner' && (
                                    <button
                                        className="btn btn-ghost btn-icon btn-icon-danger"
                                        aria-label={`Delete ${tournament.name}`}
                                        onClick={() => handleDeleteTournament(tournament)}
                                    >
                                        <TrashIcon />
                                    </button>
                                )}
                            </div>
                        </article>
                    ))}
                </div>
            )}

            {loaded && <BackupsPanel userId={user.uid} onRestored={fetchTournaments} />}
        </AppShell>
    );
}

export default Dashboard;
